import { AbstractLayoutCommand } from './AbstractLayoutCommand';
import { VideoEditor } from '../VideoEditor';
import { Element } from '../models/Element';
import { VideoEditorLogger } from '../../utility/VideoEditorLogger';
import { TimelineMode } from './TimelineMode';
import { UpdateElementCommand } from './UpdateElementCommand';

export interface RemoveLayoutElementsParameters {
  fit: 'cover' | 'contain' | 'fill';
  videoSourceWidthPrev?: number;
  videoSourceWidthCurr?: number;
}

export class ClearLayoutElementsCommand extends AbstractLayoutCommand {
  private params: RemoveLayoutElementsParameters;

  constructor(
    editor: VideoEditor,
    element: Element,
    params: RemoveLayoutElementsParameters,
  ) {
    super(editor, element);
    this.params = params;
  }

  execute(): boolean {
    if (!this.sourceElement) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'No source element provided',
      });
      return false;
    }

    VideoEditorLogger.logCommand(this, 'start', {
      elementId: this.sourceElement.id,
      extraElementData: this.extraElementData,
    });

    super.execute();

    const elements = this.pickAllLayoutElements(this.sourceElement);
    if (!elements) {
      return false;
    }
    const { parent: parentElement, children: childrenElements } = elements;

    for (const linkedElement of childrenElements) {
      this.extraElementData.removeElementData(linkedElement.id);
      const success = this.editor
        .getTrackManager()
        .removeElement(linkedElement, TimelineMode.DEFAULT);
      if (!success) {
        VideoEditorLogger.logCommand(this, 'error', {
          error: `Failed to remove element with ID ${linkedElement.id}`,
        });
        return false;
      }
    }

    this.extraElementData.clearLayoutData(parentElement);

    let command = new UpdateElementCommand(this.editor, parentElement.id, {
      visible: true,
      locked: false,
      ...this.getElementAdjustmentsForAspectRatio(parentElement),
      fit: this.params.fit,
    });
    let success = command.execute();
    if (!success) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: `Failed to update source element with ID ${parentElement.id}`,
      });
      return false;
    }

    this.canUndo = true;

    return true;
  }

  getDescription(): string {
    return `exiting layout mode`;
  }

  private getElementAdjustmentsForAspectRatio(
    element: Element,
  ): Record<'width' | 'height' | 'x' | 'y', string> {
    const result = {
      x: '50%',
      y: '50%',
      width: '100%',
      height: '100%',
    };
    const {
      // defaults are for 9:16, the only supported aspect ratio
      videoSourceWidthPrev = 1280,
      videoSourceWidthCurr = 405,
      fit,
    } = this.params;
    if (['fill', 'contain'].includes(fit)) {
      return result;
    }
    const elementWidth = 100;
    const newWidth = Math.max(
      100,
      Number(
        ((videoSourceWidthPrev * elementWidth) / videoSourceWidthCurr).toFixed(
          4,
        ),
      ),
    );
    result.width = `${newWidth}%`;
    return result;
  }
}
